import { Doc, generateToDocFn } from '@dadohr/shared/domainTypes/document';
import { OrgIntegrations } from '@dadohr/shared/domainTypes/integrations';
import { CommonIntegrationConfig } from '@dadohr/shared/domainTypes/integrations/common';
import { FS } from '@dadohr/shared/versions';
import styled from '@emotion/styled';
import { ButtonBase, Dialog, Grid, Typography } from '@material-ui/core';
import Link from '@material-ui/core/Link';
import { Link as RouterLink } from 'react-router-dom';
import { Functionality, FunctionalityLabels } from '..';
import { ColoredChipLight } from '../../../../../components/ColoredChip';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { useTranslation, useUnsafeTranslation } from '../../../../../i18next';
import { useRoutes } from '../../../../../routes';
import { store } from '../../../../../services/firebase';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../../../../../services/firecache/documentListener';

export const MASK = '●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●';
export const masked = (t: string) => (t ? MASK : '');

const configInfosCollection = () =>
  store().collection(FS.integrationConfigInfos);

export const toConfigInfosDoc = generateToDocFn<OrgIntegrations>();

export const configInfosStore = createDocumentListenerGetter((orgId) => {
  return configInfosCollection().doc(orgId);
}, toConfigInfosDoc);

export const useConfigInfos = (orgId: string) => {
  return useDocumentListener(configInfosStore(orgId));
};

const Container = styled(ButtonBase)((p) => ({
  '&&': {
    cursor: p.onClick ? 'pointer' : 'default',
    display: 'block',
    borderRadius: p.theme.custom.border.radius(),
    border: p.theme.custom.border.standard,

    backgroundColor: p.theme.custom.colors.background.light,
    ...(p.onClick
      ? {
          '&:hover': {
            boxShadow: '0 0 0 4px rgba(119,128,136,.1)'
          },
          '&:active': {
            backgroundColor: p.theme.custom.colors.gray.w100,
            boxShadow: '0 0 0 4px rgba(119,128,136,.1)'
          }
        }
      : {})
  }
}));

const InfoContainer = styled('div')((p) => ({
  paddingTop: p.theme.spacing(2),
  paddingBottom: p.theme.spacing(2)
}));

const TitleContainer = styled('div')((p) => ({
  paddingLeft: p.theme.spacing(1),
  marginBottom: p.theme.spacing(1)
}));

const ImgContainer = styled('div')((p) => ({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: p.theme.spacing(2),
  height: '100%'
}));

const Img = styled('img')<{ width?: number }>`
  width: ${(p) => (p.width ? `${p.width}px` : 'auto')};
  height: auto;
  max-height: 64px;
  max-width: 80%;
`;

const FunctionalityTypeBadges = styled('span')((p) => ({
  textAlign: 'left'
}));

const StatudBadges = styled('span')((p) => ({
  padding: p.theme.spacing(1)
}));

const ActiveBadge = () => {
  const { t } = useTranslation();
  return (
    <ColoredChipLight color="green" size="small">
      {t('org.integrations.active')}
    </ColoredChipLight>
  );
};

const FunctionalityTypeBadgeContainer = styled('div')((p) => ({
  padding: p.theme.spacing(0.5),
  display: 'inline-block'
}));

const FunctionalityTypeBadge = ({ label }: { label: string }) => (
  <FunctionalityTypeBadgeContainer>
    <ColoredChipLight color="gray" size="small">
      {label}
    </ColoredChipLight>
  </FunctionalityTypeBadgeContainer>
);

export type SetupDialogProperties = {
  onSucessfulIntegration: () => void;
  open: boolean;
};

export type IntegrationSetupDialog<
  P extends SetupDialogProperties
> = React.ComponentType<P>;

export type IntegrationSetupComponent = (properties: {
  functionalities: Functionality[];
}) => JSX.Element;

// console.log(FunctionalityLabels[Functionality.AUTOMATIONS]);

const Content = styled('div')((p) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingTop: p.theme.spacing(7),
  paddingBottom: p.theme.spacing(7),
  paddingLeft: p.theme.spacing(3),
  paddingRight: p.theme.spacing(3)
}));

const ModalTitle = styled('div')((p) => ({
  marginBottom: p.theme.spacing(2)
}));

const ModalContent = styled('div')((p) => ({
  marginLeft: p.theme.spacing(8),
  marginRight: p.theme.spacing(8)
}));

export const IntegrationSetupCompleteModal = ({
  integrationName,
  open,
  onClose
}: {
  integrationName: string;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { ROUTES } = useRoutes();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Content>
        <ModalTitle>
          <Typography component="h2" variant="h3" align="center">
            {t('Integration with {{integrationName}} complete', {
              integrationName
            })}
          </Typography>
        </ModalTitle>
        <ModalContent>
          <Typography component="div" variant="subtitle1" align="center">
            {t('Head to ')}

            <Link
              component={RouterLink}
              to={ROUTES.users.imports.importSources.url({
                addSourceOpen: true
              })}
            >
              <Typography component="span" variant="subtitle1">
                <b>{t('User → Import Sources')}</b>
              </Typography>
            </Link>
            {t(
              ' to select user data to import and configure other import settings.'
            )}
          </Typography>
        </ModalContent>
      </Content>
    </Dialog>
  );
};

export const IntegrationSetupFailureModal = ({
  integrationName,
  open,
  onClose
}: {
  integrationName: string;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useUnsafeTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Content>
        <ModalTitle>
          <Typography component="h2" variant="h3" align="center">
            {t('Something went wrong!')}
          </Typography>
        </ModalTitle>
        <ModalContent>
          <Typography component="div" variant="subtitle1" align="center">
            {t(
              'There was an issue connecting to {{integrationName}}. Our team has been notified. In the meantime, please close the window and start the process again.',
              {
                integrationName
              }
            )}
          </Typography>
        </ModalContent>
      </Content>
    </Dialog>
  );
};

export interface IntegrationSetupDialogProperties {
  onSuccessfulIntegration: () => void;
  onFailure: (error?: string) => void;
  onClose: () => void;
  open: boolean;
}

export interface IntegrationSetupDialogWithConfigProperties<
  T extends CommonIntegrationConfig
> extends IntegrationSetupDialogProperties {
  config: Doc<T> | null;
}

export const IntegrationButton = ({
  showSatus,
  name,
  active,
  interactive,
  logoUrl,
  functionalities,
  onClick
}: {
  name: string;
  showSatus: boolean;
  active?: boolean;
  interactive: boolean;
  logoUrl: string;
  functionalities: Functionality[];
  onClick: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <Container onClick={interactive ? onClick : undefined}>
      <Grid container direction="row" spacing={0}>
        <Grid item>
          <ImgContainer>
            {logoUrl && <Img src={logoUrl} alt={name} width={100} />}
          </ImgContainer>
        </Grid>
        <Grid item xs={12} direction="column" sm container>
          <InfoContainer>
            <Grid item container xs>
              <TitleContainer>
                <Typography variant="h5" component="div">
                  {name}
                  {showSatus && (
                    <StatudBadges>{active && <ActiveBadge />}</StatudBadges>
                  )}
                </Typography>
              </TitleContainer>
            </Grid>
            <Grid item container xs>
              <FunctionalityTypeBadges>
                {functionalities.map((f) => (
                  <FunctionalityTypeBadge
                    key={f}
                    label={FunctionalityLabels[f](t)}
                  />
                ))}
              </FunctionalityTypeBadges>
            </Grid>
          </InfoContainer>
        </Grid>
      </Grid>
    </Container>
  );
};

export const IntegrationConfigurationButton = ({
  name,
  active,
  interactive,
  logoUrl,
  functionalities,
  renderSetupDialog: renderDialog
}: {
  name: string;
  active: boolean;
  interactive: boolean;
  logoUrl: string;
  functionalities: Functionality[];
  renderSetupDialog: <P extends IntegrationSetupDialogProperties>(
    props: IntegrationSetupDialogProperties
  ) => React.ReactElement<P>;
}) => {
  const { dialogOpen, openDialog, closeDialog } = useDialogState(false);
  //const user = useCurrentUser();
  const {
    dialogOpen: successDialopOpen,
    openDialog: openSuccessDialog,
    closeDialog: closeSuccessDialog
  } = useDialogState(false);

  const {
    dialogOpen: failureDialopOpen,
    openDialog: openFailureDialog,
    closeDialog: closefailureDialog
  } = useDialogState(false);

  const onSuccessfulIntegration = () => {
    // Right now we only open the Modal to direct people to the import sources
    // so we only do it if the integrations provides an import source
    if (functionalities.includes(Functionality.IMPORT_SOURCE)) {
      closeDialog();
      openSuccessDialog();
    }
    // MAYBE it would be  better to create the add Integration customer action here
    // Problme being that some integrations are finished in a different tab and people might close this one

    // const integrationAddedAction: IntegrationAddedAction = {
    //   action: CUSTOMER_ACTIONS.INTEGRATION_ADDED,
    //   adminUserId: user.authUser?.uid || '',
    //   integrationName: name,
    //   orgId: user.org.id
    // };

    // tryReportCustommerAction(integrationAddedAction);
  };

  const onFailure = (error?: string) => {
    openFailureDialog();
  };

  return (
    <>
      <IntegrationButton
        onClick={openDialog}
        logoUrl={logoUrl}
        active={active}
        interactive={interactive}
        showSatus={true}
        functionalities={functionalities}
        name={name}
      />
      {renderDialog &&
        renderDialog({
          open: dialogOpen,
          onClose: closeDialog,
          onFailure,
          onSuccessfulIntegration
        })}
      <IntegrationSetupCompleteModal
        integrationName={name}
        open={successDialopOpen}
        onClose={closeSuccessDialog}
      />
      <IntegrationSetupFailureModal
        integrationName={name}
        open={failureDialopOpen}
        onClose={closefailureDialog}
      />
    </>
  );
};
